// src/pages/admin/AdminUsers.jsx — FINAL, no-delay scroll & edit
import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import Card from "../../components/Card.jsx";
import Button from "../../components/Button.jsx";
import Input from "../../components/Input.jsx";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import Modal from "../../components/Modal.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import {
  Users,
  Plus,
  Pencil as Edit3,
  Trash2,
  MoreVertical,
  Shield,
  User,
  Mail,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { api } from "../../lib/api.js";

/* ------------------------------ Helpers ------------------------------ */

const normRole = (row) => {
  // backend bisa kirim "role" string atau boolean "is_admin"/"isAdmin"
  if (typeof row?.role === "string") {
    return row.role.toLowerCase().includes("admin") ? "admin" : "user";
  }
  if (row?.is_admin != null) return row.is_admin ? "admin" : "user";
  if (row?.isAdmin != null) return row.isAdmin ? "admin" : "user";
  return "user";
};
const toBoolAdmin = (role) => role === "admin";

/*  Endpoint base yang adaptif:
    - coba '/admin/users' dulu
    - kalau 404, fallback ke '/users'
*/
async function tryCall(method, url, configOrData) {
  if (method === "get" || method === "delete") {
    return api[method](url, configOrData);
  }
  // post/put
  return api[method](url, configOrData);
}

/* ------------------------------ Component ------------------------------ */

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });

  const [submitting, setSubmitting] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // base endpoint yang disimpan setelah deteksi (default ke /admin/users)
  const [usersBase, setUsersBase] = useState("/admin/users");

  const [searchTerm, setSearchTerm] = useState('');

  const [userForm, setUserForm] = useState({
    username: "",
    email: "",
    password: "",
    role: "user",
  });

  useEffect(() => {
    fetchUsers(); // initial load: tampilkan spinner sekali
  }, []);

  const fetchUsers = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // 1) coba ke /admin/users
      let res;
      try {
        res = await tryCall("get", usersBase);
      } catch (e) {
        if (e?.response?.status === 404 && usersBase !== "/users") {
          // fallback ke /users
          setUsersBase("/users");
          res = await tryCall("get", "/users");
        } else {
          throw e;
        }
      }
      const data = Array.isArray(res.data) ? res.data : [];
      // normalisasi user
      const norm = data.map((row) => ({
        id: row.id,
        username: row.username || "",
        email: row.email || "",
        role: normRole(row),
        created_at: row.created_at || row.createdAt || null,
      }));
      setUsers(norm);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error(error.response?.data?.error || "Failed to load users");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const resetForm = () => {
    setUserForm({
      username: "",
      email: "",
      password: "",
      role: "user",
    });
  };

  const handleAdd = () => {
    resetForm();
    setAddModal(true);
  };

  const handleEdit = (user) => {
    setUserForm({
      username: user.username,
      email: user.email || "",
      password: "",
      role: user.role || "user",
    });
    setEditModal({ open: true, user });
    setActiveDropdown(null);
  };

  const handleDelete = (user) => {
    setDeleteDialog({ open: true, user });
    setActiveDropdown(null);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        username: userForm.username,
        email: userForm.email,
        password: userForm.password,
        role: userForm.role, // untuk backend yang pakai "role"
        is_admin: toBoolAdmin(userForm.role), // untuk backend yang pakai boolean "is_admin"
      };

      let created;
      try {
        created = await tryCall("post", usersBase, payload);
      } catch (e) {
        if (e?.response?.status === 404 && usersBase !== "/users") {
          setUsersBase("/users");
          created = await tryCall("post", "/users", payload);
        } else {
          throw e;
        }
      }

      // Optimistic append (kalau server balikin objek, ambil id; kalau tidak, tetap refresh silent)
      const createdRow = created?.data;
      if (createdRow?.id) {
        setUsers((prev) => [
          {
            id: createdRow.id,
            username: createdRow.username ?? payload.username,
            email: createdRow.email ?? payload.email,
            role: normRole(createdRow ?? payload),
            created_at: createdRow.created_at || createdRow.createdAt || null,
          },
          ...prev,
        ]);
      }

      toast.success("User created successfully!");
      setAddModal(false);
      resetForm();

      // Refresh silent agar sinkron tanpa spinner layar penuh
      fetchUsers({ silent: true });
    } catch (error) {
      const message = error.response?.data?.error || "Failed to create user";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editModal.user) return;

    setSubmitting(true);
    try {
      const id = editModal.user.id;
      const updateData = {
        username: userForm.username,
        email: userForm.email,
        role: userForm.role,
        is_admin: toBoolAdmin(userForm.role),
      };
      if (userForm.password) updateData.password = userForm.password;

      try {
        await tryCall("put", `${usersBase}/${id}`, updateData);
      } catch (e) {
        if (e?.response?.status === 404 && usersBase !== "/users") {
          setUsersBase("/users");
          await tryCall("put", `/users/${id}`, updateData);
        } else {
          throw e;
        }
      }

      // Optimistic update: langsung update item di list
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id
            ? {
                ...u,
                username: updateData.username,
                email: updateData.email,
                role: updateData.role,
              }
            : u
        )
      );

      toast.success("User updated successfully!");
      setEditModal({ open: false, user: null });
      resetForm();

      // Refresh silent untuk konsistensi tanpa spinner layar penuh
      fetchUsers({ silent: true });
    } catch (error) {
      const message = error.response?.data?.error || "Failed to update user";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.user) return;
    setSubmitting(true);
    try {
      const id = deleteDialog.user.id;
      try {
        await tryCall("delete", `${usersBase}/${id}`);
      } catch (e) {
        if (e?.response?.status === 404 && usersBase !== "/users") {
          setUsersBase("/users");
          await tryCall("delete", `/users/${id}`);
        } else {
          throw e;
        }
      }

      // Optimistic remove
      setUsers((prev) => prev.filter((u) => u.id !== id));

      toast.success("User deleted successfully!");
      setDeleteDialog({ open: false, user: null });

      // Refresh silent agar data tetap akurat
      fetchUsers({ silent: true });
    } catch (error) {
      const message = error.response?.data?.error || "Failed to delete user";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = [u.username, u.email, u.role].map((v) => String(v || '').toLowerCase());
      return hay.some((v) => v.includes(q));
    });
  }, [users, searchTerm]);

  const UserCard = React.memo(({ user }) => (
    <motion.div
      /* Hindari opacity:0 supaya tidak muncul blank hitam */
      initial={false}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.18 }}
      className="relative"
    >
      <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-1 sm:gap-3 mb-2 sm:mb-4">
          <div className="flex items-start gap-2 sm:gap-3 min-w-0">
            <div
              className={`p-1.5 sm:p-3 rounded-lg ${
                user.role === "admin" ? "bg-purple-600" : "bg-blue-600"
              }`}
            >
              {user.role === "admin" ? (
                <Shield className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              ) : (
                <User className="h-6 w-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm sm:text-lg">{user.username}</h3>
              {user.email ? (
                <p className="text-gray-400 text-[10px] sm:text-sm flex items-center truncate max-w-[80px] sm:max-w-none">
                  <Mail className="h-4 w-4 mr-1" />
                  {user.email}
                </p>
              ) : (
                <p className="text-gray-500 text-xs sm:text-sm italic">no email</p>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-1 shrink-0">
            <span
              className={`px-1.5 py-0.5 rounded-full text-[9px] sm:text-xs font-medium capitalize ${
                user.role === "admin"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {user.role}
            </span>

            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveDropdown((cur) => (cur === user.id ? null : user.id));
                }}
                className="p-1 sm:p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <MoreVertical className="h-4 w-4" />
              </button>

              <AnimatePresence>
                {activeDropdown === user.id && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    className="absolute right-0 top-full mt-1 w-40 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10"
                  >
                    <button
                      onClick={() => handleEdit(user)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2 rounded-t-lg"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-600/10 hover:text-red-300 flex items-center space-x-2 rounded-b-lg"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Delete</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="text-[10px] sm:text-sm text-gray-400">
          <p>
            Member since{" "}
            {user.created_at
              ? format(
                  typeof user.created_at === "string"
                    ? parseISO(user.created_at)
                    : user.created_at,
                  "MMM dd, yyyy"
                )
              : "-"}
          </p>
        </div>
      </Card>
    </motion.div>
  ));
  UserCard.displayName = "UserCard";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  if (loading) {
    // Spinner hanya saat initial load, bukan saat refresh kecil
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center">
            <Users className="h-8 w-8 text-purple-400 mr-3" />
            Manage Users
          </h1>
          <p className="text-gray-400">
            Create, edit, and manage user accounts and permissions
          </p>
        </div>
        <Button onClick={handleAdd} className="flex items-center">
          <Plus className="h-5 w-5 mr-2" />
          Add User
        </Button>
      </motion.div>

      <div className="w-full max-w-md">
        <Input
          label="Search users"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or role"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-purple-800 to-purple-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-200 text-sm">Total Users</p>
              <p className="text-3xl font-bold text-white">{users.length}</p>
            </div>
            <Users className="h-8 w-8 text-purple-200" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-sm">Regular Users</p>
              <p className="text-3xl font-bold text-white">
                {users.filter((u) => u.role === "user").length}
              </p>
            </div>
            <User className="h-8 w-8 text-blue-200" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-amber-800 to-amber-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-200 text-sm">Administrators</p>
              <p className="text-3xl font-bold text-white">
                {users.filter((u) => u.role === "admin").length}
              </p>
            </div>
            <Shield className="h-8 w-8 text-amber-200" />
          </div>
        </Card>
      </div>

      {/* Users List */}
      <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-6">
        {filteredUsers.map((u) => (
          <UserCard key={u.id} user={u} />
        ))}
      </div>

      {users.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <Users className="h-16 w-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No users found</h3>
          <p className="text-gray-400 mb-6">
            Start by adding your first user to the system.
          </p>
          <Button onClick={handleAdd}>Add First User</Button>
        </motion.div>
      ) : filteredUsers.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <Users className="h-12 w-12 text-gray-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-white mb-2">No matches found</h3>
          <p className="text-gray-400">Try a different search.</p>
        </motion.div>
      ) : null}

      {/* Add User Modal */}
      <Modal
        isOpen={addModal}
        onClose={() => setAddModal(false)}
        title="Add New User"
        size="md"
      >
        <form onSubmit={submitAdd} className="space-y-4">
          <Input
            label="Username"
            value={userForm.username}
            onChange={(e) =>
              setUserForm((p) => ({ ...p, username: e.target.value }))
            }
            placeholder="Enter username"
            required
          />

          <Input
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e) =>
              setUserForm((p) => ({ ...p, email: e.target.value }))
            }
            placeholder="Enter email address"
            required
          />

          <Input
            label="Password"
            type="password"
            value={userForm.password}
            onChange={(e) =>
              setUserForm((p) => ({ ...p, password: e.target.value }))
            }
            placeholder="Enter password"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Role
            </label>
            <select
              value={userForm.role}
              onChange={(e) =>
                setUserForm((p) => ({ ...p, role: e.target.value }))
              }
              className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAddModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting} className="flex-1">
              Add User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, user: null })}
        title="Edit User"
        size="md"
      >
        <form onSubmit={submitEdit} className="space-y-4">
          <Input
            label="Username"
            value={userForm.username}
            onChange={(e) =>
              setUserForm((p) => ({ ...p, username: e.target.value }))
            }
            placeholder="Enter username"
            required
          />

          <Input
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e) =>
              setUserForm((p) => ({ ...p, email: e.target.value }))
            }
            placeholder="Enter email address"
            required
          />

          <Input
            label="New Password (Optional)"
            type="password"
            value={userForm.password}
            onChange={(e) =>
              setUserForm((p) => ({ ...p, password: e.target.value }))
            }
            placeholder="Leave blank to keep current password"
          />

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Role
            </label>
            <select
              value={userForm.role}
              onChange={(e) =>
                setUserForm((p) => ({ ...p, role: e.target.value }))
              }
              className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditModal({ open: false, user: null })}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting} className="flex-1">
              Update User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
        onConfirm={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteDialog.user?.username}? This action cannot be undone.`}
        type="danger"
        confirmText="Delete User"
        loading={submitting}
      />
    </div>
  );
}

export default AdminUsers;
